import moment from "moment";
import { DatePickerInput } from "rc-datepicker";
import { useEffect, useState } from "react";
import { Badge, Form } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { MdFileDownload } from "react-icons/md";
import Loading from "../../components/Loading";
import { getImportCsv, getImportHistory, getImportRequesters } from "../../services/NexiDigitalAPI";

export const UserImportHistory = () => {
  const [loading, setLoading] = useState(true);
  const [imports, setImports] = useState([]);
  const [requesters, setRequesters] = useState([]);
  const paginationComponentOptions = {
    rowsPerPageText: "Importações por página",
    rangeSeparatorText: "de",
  };

  const [pagination, setPagination] = useState({
    offset: 0,
    limit: 10,
    totalRecords: 999,
  });

  const [searchData, setSearchData] = useState({
    createdBy: null,
    dateFrom: "",
    dateTo: "",
    statusList: [],
  });

  const [downloading, setDownloading] = useState(null);

  const loadImports = async (_pagination, _searchData) => {
    setSearchData(_searchData);
    const result = await getImportHistory(
      _pagination.limit,
      _pagination.offset,
      _searchData.createdBy,
      _searchData.dateFrom,
      _searchData.dateTo,
      _searchData.statusList,
    );
    setPagination({ ..._pagination, totalRecords: result.data.totalRecords });
    setImports(result.data?.result);
    setLoading(false);
  };

  const loadRequesters = async () => {
    const response = await getImportRequesters();
    setRequesters(response.data.result);
  };

  useEffect(() => {
    loadRequesters();
    loadImports(pagination, searchData);
  }, []);

  const columns = [
    {
      name: "Data e hora",
      selector: "creationDate",
      wrap: false,
      sortable: false,
      style: { textAlign: "left" },
      maxWidth: "150px",
      cell: (d) => <>{moment(d.creationDate).utc().format("DD/MM/YYYY - HH:mm") || "-"}</>,
    },
    {
      name: "Realizado por",
      selector: "createdByName",
      wrap: true,
      sortable: false,
      style: { textAlign: "left" },
      cell: (d) => <>{d.createdByName ? `${d.createdByName} - ${d.createdByEmail}` : "-"}</>,
    },
    {
      name: "Origem",
      selector: "isIntegration",
      wrap: false,
      sortable: false,
      style: { textAlign: "left" },
      maxWidth: "150px",
      cell: (d) => <>{d.isIntegration ? "Integração" : "Upload de arquivo"}</>,
    },
    {
      name: "Total de Registros",
      selector: "totalRecords",
      wrap: false,
      sortable: false,
      style: { textAlign: "center" },
      maxWidth: "150px",
    },
    {
      name: "Total de Processados",
      selector: "totalRecords",
      wrap: false,
      sortable: false,
      style: { textAlign: "center" },
      maxWidth: "180px",
      cell: (d) => <>{(d.totalSuccess ?? 0) + (d.totalError ?? 0)}</>,
    },
    {
      name: "Status",
      wrap: false,
      center: false,
      style: { textAlign: "left" },
      maxWidth: "10px",
      reorder: false,
      sortable: false,
      cell: (d) =>
        d.status === "NOT-STARTED" ? (
          <Badge variant="secondary" style={{ borderRadius: "20px" }}>
            Não processado
          </Badge>
        ) : d.status === "STARTED" ? (
          <Badge variant="warning" style={{ borderRadius: "20px" }}>
            Processando
          </Badge>
        ) : d.status === "ERROR" ? (
          <Badge variant="danger" style={{ borderRadius: "20px" }}>
            Completo com erros
          </Badge>
        ) : (
          <Badge variant="success" style={{ borderRadius: "20px" }}>
            Completo
          </Badge>
        ),
    },
    {
      name: "Detalhes",
      wrap: true,
      width: "200px",
      center: true,
      style: { textAlign: "center" },
      cell: (item) => {
        return (
          <div style={{ textAlign: "center" }}>
            {(downloading ?? 0) === item.idImport && <span>baixando...</span>}
            {(downloading ?? 0) !== item.idImport &&
              ["FINISHED", "ERROR"].includes(item.status) && (
                <MdFileDownload
                  onClick={() => downloadCsv(item.idImport)}
                  style={{
                    cursor: "pointer",
                    fontSize: 22,
                  }}
                />
              )}
          </div>
        );
      },
    },
  ];

  const onChangePage = (page) => {
    loadImports(
      {
        ...pagination,
        offset: page === 1 ? 0 : (page - 1) * pagination.limit,
      },
      searchData,
    );
  };

  const onChangeRowsPerPage = (limit, page) => {
    loadImports(
      {
        ...pagination,
        limit,
        offset: 0,
      },
      searchData,
    );
  };

  const handleValidateDate = (date, field) => {
    const _searchData = Object.assign(searchData, {});
    const inputValue = moment(date, "YYYY-MM-DD", true);
    if (!inputValue.isValid()) {
      _searchData[field] = "";
    } else {
      _searchData[field] = inputValue;
    }
    loadImports(pagination, _searchData);
  };

  const handleCheckboxChange = (value, checked) => {
    if (value === "all") {
      loadImports(pagination, { ...searchData, statusList: checked ? ["all"] : [] });
    } else {
      loadImports(pagination, {
        ...searchData,
        statusList: checked
          ? searchData.statusList.filter((s) => s !== value && s !== "all").concat([value])
          : searchData.statusList.filter((s) => s !== value && s !== "all"),
      });
    }
  };

  const downloadCsv = async (importId) => {
    setDownloading(importId);
    const response = await getImportCsv(importId);
    const fileName =
      (response.headers["content-disposition"]?.match(/filename="([^"]+)"/) ?? [])[1] ??
      "exported.csv";
    const href = URL.createObjectURL(response.data);

    const link = document.createElement("a");
    link.href = href;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(href);
    setDownloading(null);
  };

  return (
    <div>
      <h5>Histórico de importações</h5>
      <div className="search-bar">
        <div className="group">
          <div>Realizado por</div>
          <Form.Control
            as="select"
            value={searchData?.createdBy}
            style={{ maxWidth: "300px" }}
            onChange={(e) =>
              loadImports(pagination, {
                ...searchData,
                createdBy: parseInt(e.target.value),
              })
            }
          >
            <option value="">Selecione</option>
            {requesters.map((r) => (
              <option value={r.idUser}>
                {r.name} - {r.email}
              </option>
            ))}
          </Form.Control>
          <div>De</div>
          <DatePickerInput
            placeholder="DD/MM/AAAA"
            className="input-DatePickerInput"
            locale="pt-br"
            onChange={(date) => handleValidateDate(date, "dateFrom")}
            value={moment(searchData.dateFrom).isValid() ? searchData.dateFrom : undefined}
          />
          <div>Até</div>
          <DatePickerInput
            placeholder="DD/MM/AAAA"
            className="input-DatePickerInput"
            locale="pt-br"
            onChange={(date) => handleValidateDate(date, "dateTo")}
            value={moment(searchData.dateTo).isValid() ? searchData.dateTo : undefined}
          />
        </div>
        <div className="group">
          <Form.Check
            onChange={(e) => handleCheckboxChange("all", e.target.checked)}
            checked={searchData.statusList?.includes("all")}
            type="checkbox"
            label={`Todos`}
          />
          <Form.Check
            onChange={(e) => handleCheckboxChange("STARTED", e.target.checked)}
            checked={searchData.statusList?.includes("STARTED")}
            type="checkbox"
            label={`Processando`}
          />
          <Form.Check
            onChange={(e) => handleCheckboxChange("ERROR", e.target.checked)}
            checked={searchData.statusList?.includes("ERROR")}
            type="checkbox"
            label={`Completo com erros`}
          />
          <Form.Check
            onChange={(e) => handleCheckboxChange("NOT-STARTED", e.target.checked)}
            checked={searchData.statusList?.includes("NOT-STARTED")}
            type="checkbox"
            label={`Não processado`}
          />
          <Form.Check
            onChange={(e) => handleCheckboxChange("FINISHED", e.target.checked)}
            checked={searchData.statusList?.includes("FINISHED")}
            type="checkbox"
            label={`Completo`}
          />
        </div>
      </div>

      <div className="node-manager-import-table-wrapper mb-2">
        {loading && (
          <div className="no-imports-message">
            <b>Carregando...</b>
          </div>
        )}
        {!loading && (
          <>
            {(imports ?? []).length === 0 && (
              <div className="no-imports-message">
                <b>Ainda não há importações de usuários</b>
                <br />
                Baixe o modelo CSV, preencha os dados e faça o upload para importar usuários.
              </div>
            )}

            <DataTable
              columns={columns}
              data={imports}
              progressComponent={
                <Loading containerStyle={{ height: 536 }}>Carregando importações...</Loading>
              }
              onChangePage={onChangePage}
              onChangeRowsPerPage={onChangeRowsPerPage}
              paginationTotalRows={pagination.totalRecords}
              paginationComponentOptions={paginationComponentOptions}
              progressPending={loading && imports?.length === 0}
              paginationServer
              noHeader
              fixedHeader
              pagination
              highlightOnHover
            />
          </>
        )}
      </div>
    </div>
  );
};
